'use client';

import { useState, useRef, useCallback, ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { IconExternalLink, IconAlertCircle, IconX } from '@tabler/icons-react';
import { safeHref } from '@/lib/safe-href';

const LOAD_TIMEOUT_MS = 12000;

interface ProjectLinkDialogProps {
  url: string;
  title?: string;
  trigger: ReactNode;
}

export function ProjectLinkDialog({ url, title, trigger }: ProjectLinkDialogProps) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const safeUrl = safeHref(url);
  const isValidUrl = safeUrl !== '#';

  const hostname = (() => {
    try {
      return new URL(url).hostname;
    } catch {
      return url;
    }
  })();

  const handleOpenChange = useCallback((next: boolean) => {
    if (next) {
      setStatus(isValidUrl ? 'loading' : 'error');
      if (isValidUrl) {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          setStatus((prev) => (prev === 'loading' ? 'error' : prev));
        }, LOAD_TIMEOUT_MS);
      }
    } else {
      if (timerRef.current) clearTimeout(timerRef.current);
    }
    setOpen(next);
  }, [isValidUrl]);

  const handleLoad = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setStatus('loaded');
  }, []);

  const handleError = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setStatus('error');
  }, []);

  return (
    <>
      <span onClick={() => handleOpenChange(true)} className="contents">
        {trigger}
      </span>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="h-[95vh] flex flex-col p-0 gap-0" style={{ width: '80vw', maxWidth: 'none' }}>
          {/* Header */}
          <DialogHeader className="px-6 pt-5 pb-3 shrink-0">
            <div className="flex items-start gap-3 min-w-0">
              <div className="flex-1 min-w-0">
                <DialogTitle className="text-base font-semibold leading-snug line-clamp-1">
                  {title ?? hostname}
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground mt-0.5 truncate">
                  {hostname}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <Separator />

          {/* Iframe area */}
          <div className="relative flex-1 min-h-0 bg-muted/20">
            {/* Loading overlay */}
            {status === 'loading' && (
              <div className="absolute inset-0 flex items-center justify-center z-10 bg-background/80">
                <div className="flex flex-col items-center gap-3 text-center">
                  <div className="size-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                  <p className="text-sm text-muted-foreground">Loading page…</p>
                </div>
              </div>
            )}

            {/* Error state */}
            {status === 'error' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center px-6 z-10 bg-background">
                <IconAlertCircle className="size-10 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Unable to display this page in the preview.</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    The site may block embedding. Open it directly instead.
                  </p>
                </div>
                <Button size="sm" asChild>
                  <a href={safeUrl} target="_blank" rel="noopener noreferrer">
                    <IconExternalLink className="size-4 mr-1.5" />
                    Open in new tab
                  </a>
                </Button>
              </div>
            )}

            {/* Iframe — always mounted when url is valid so load/error fires */}
            {isValidUrl && (
              <iframe
                key={url}
                src={safeUrl}
                title={title ?? hostname}
                allow="clipboard-write; fullscreen"
                loading="lazy"
                onLoad={handleLoad}
                onError={handleError}
                className="w-full h-full border-0"
                style={{ display: status === 'error' ? 'none' : 'block' }}
              />
            )}
          </div>

          <Separator />

          {/* Footer */}
          <DialogFooter className="px-6 py-3 shrink-0 flex-row justify-between sm:justify-between gap-2">
            <Button variant="outline" size="sm" asChild>
              <a href={safeUrl} target="_blank" rel="noopener noreferrer">
                <IconExternalLink className="size-4 mr-1.5" />
                Open in new tab
              </a>
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              <IconX className="size-4 mr-1.5" />
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
